import { useRef, useMemo, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * ArtemisChaseCamera - Renders to texture
 * Embedded as child of Artemis group, positioned above and behind
 * Uses manual render target to capture the main scene
 */
export function ArtemisChaseCamera({ 
  onTextureUpdate 
}) {
  const { gl, scene } = useThree()
  const cameraRef = useRef()
  const renderTargetRef = useRef()
  const groupRef = useRef()
  const isMountedRef = useRef(true)
  const timeoutIdRef = useRef(null)
  
  // Camera offset (above and behind the rocket, in local space)
  const cameraOffset = useRef(new THREE.Vector3(-20.0, 10.0, -20.0))
  const lookAtOffset = useRef(new THREE.Vector3(0, 0, 0.0))
  
  // Reusable Vector3 for lookAt calculations to avoid creating new objects every frame
  const lookAtWorldRef = useRef(new THREE.Vector3())
  
  useEffect(() => {
    // Create render target with sRGB color space for proper brightness
    const renderTarget = new THREE.WebGLRenderTarget(320, 240, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      samples: 4, // 4x MSAA for anti-aliasing
      generateMipmaps: false,
      colorSpace: THREE.SRGBColorSpace, // Render in sRGB space directly
      // WebGPU-specific texture usage hints
      usage: gl.isWebGPURenderer ? 
        THREE.TextureBinding | THREE.CopyDst | THREE.RenderAttachment : // WebGPU: explicit usage flags with render attachment
        undefined // WebGL: uses default usage
    })
    renderTargetRef.current = renderTarget
    
    // Create chase camera
    const camera = new THREE.PerspectiveCamera(35, 320 / 240, 0.01, 100)
    // Only render layer 0 (default), excluding layer 1 (trajectory)
    camera.layers.set(0)
    cameraRef.current = camera
    
    // Send both render target and texture
    if (onTextureUpdate) {
      onTextureUpdate({ 
        renderTarget: renderTarget,
        texture: renderTarget.texture
      })
    }
    
    return () => {
      renderTarget.dispose()
    }
  }, [onTextureUpdate])
  
  useFrame((state, delta) => {
    if (!cameraRef.current || !renderTargetRef.current || !groupRef.current) return
    if (!isMountedRef.current) return
    
    // Camera is embedded in Artemis group, so positions are in local space
    // Set camera position to offset behind/above Artemis (in local coords)
    cameraRef.current.position.copy(cameraOffset.current)
    
    // Look at a point ahead of Artemis (in local coords)
    // Reuse lookAtWorldRef instead of creating new Vector3 every frame
    lookAtWorldRef.current.copy(lookAtOffset.current)
    
    // Convert local lookAt to world space for camera.lookAt()
    groupRef.current.localToWorld(lookAtWorldRef.current)
    cameraRef.current.lookAt(lookAtWorldRef.current)
    
    // WEBGPU FIX: Defer render-to-texture to avoid synchronization conflicts
    // This ensures the texture write/read operations don't happen in the same scope
    if (gl.isWebGPURenderer) {
      // Schedule render-to-texture for after main render completes
      // Clear any pending timeout first
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
      timeoutIdRef.current = setTimeout(() => {
        if (!isMountedRef.current) return
        if (renderTargetRef.current && cameraRef.current) {
          const originalRenderTarget = gl.getRenderTarget()
          gl.setRenderTarget(renderTargetRef.current)
          gl.render(scene, cameraRef.current)
          gl.setRenderTarget(originalRenderTarget)
          // NOTE: onTextureUpdate is intentionally NOT called here every frame.
          // The render target object is stable — ArtemisChaseCameraView reads its
          // GPU texture directly in its own useFrame without needing React re-renders.
        }
      }, 0)
    } else {
      // WebGL can handle synchronous render-to-texture
      const originalRenderTarget = gl.getRenderTarget()
      gl.setRenderTarget(renderTargetRef.current)
      gl.render(scene, cameraRef.current)
      gl.setRenderTarget(originalRenderTarget)
    }
  })
  
  // Add camera to group imperatively
  useEffect(() => {
    if (cameraRef.current && groupRef.current) {
      groupRef.current.add(cameraRef.current)
      
      return () => {
        if (groupRef.current && cameraRef.current) {
          groupRef.current.remove(cameraRef.current)
        }
      }
    }
  }, [])
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    
    return () => {
      isMountedRef.current = false
      
      // Clear pending timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }
      
      // Notify parent to clear render target reference (prevents memory leak)
      if (onTextureUpdate) {
        onTextureUpdate(null)
      }
      
      // Dispose render target
      if (renderTargetRef.current) {
        renderTargetRef.current.dispose()
        renderTargetRef.current = null
      }
      
      // Dispose camera
      if (cameraRef.current) {
        cameraRef.current = null
      }
      
      // Clear Vector3 refs
      cameraOffset.current = null
      lookAtOffset.current = null
      lookAtWorldRef.current = null
      
      // Clear group ref
      groupRef.current = null
    }
  }, [onTextureUpdate])
  
  return <group ref={groupRef} />
}
